import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Users, 
    Search, 
    Plus, 
    Filter, 
    ChevronRight,
    ChevronDown,
    Mail,
    Phone,
    Copy,
    Check,
    ArrowLeft,
    MoreHorizontal,
    Upload,
    AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Member status options with colors
const MEMBER_STATUSES = {
    active: { color: 'bg-green-500', label: 'Active', textColor: 'text-green-700' },
    probation: { color: 'bg-blue-500', label: 'Probation', textColor: 'text-blue-700' },
    reserve: { color: 'bg-purple-500', label: 'Reserve', textColor: 'text-purple-700' },
    inactive: { color: 'bg-red-500', label: 'Inactive', textColor: 'text-red-700' },
    applicant: { color: 'bg-amber-500', label: 'Applicant', textColor: 'text-amber-700' },
    support: { color: 'bg-teal-500', label: 'Support', textColor: 'text-teal-700' },
};

export default function MembersPage() {
    const navigate = useNavigate();
    const { canCreateUsers } = useAuth();
    const [members, setMembers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showGroupDropdown, setShowGroupDropdown] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [copied, setCopied] = useState(false);
    const [newMember, setNewMember] = useState({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'general_member',
        group_id: '',
        member_status: 'active'
    });
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            const [membersRes, groupsRes, rolesRes] = await Promise.all([
                axios.get(`${API_URL}/users`),
                axios.get(`${API_URL}/groups`),
                axios.get(`${API_URL}/admin/roles`)
            ]);
            setMembers(membersRes.data);
            setGroups(groupsRes.data);
            setAvailableRoles(rolesRes.data.roles || []);
        } catch (error) {
            console.error('Failed to fetch members:', error);
            toast.error('Failed to load members');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddMember = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_URL}/admin/users`, newMember);
            setWelcomeMessage(response.data.welcome_message);
            setShowAddDialog(false);
            setShowWelcomeDialog(true);
            setNewMember({
                username: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                role: 'general_member',
                group_id: '',
                member_status: 'active'
            });
            fetchData();
            toast.success('Member created successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add member');
        }
    };
    
    const handleCopyWelcome = async () => {
        try {
            await navigator.clipboard.writeText(welcomeMessage);
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Failed to copy');
        }
    };

    const getGroupName = (groupId) => {
        const group = groups.find(g => g.id === groupId);
        return group?.name || '-';
    };

    const getMemberStatus = (member) => {
        // Use member_status field from database, default to active
        return member.member_status || 'active';
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
    const filteredMembers = members.filter(member => {
        const matchesSearch = 
            member.first_name?.toLowerCase().includes(search.toLowerCase()) ||
            member.last_name?.toLowerCase().includes(search.toLowerCase()) ||
            member.email?.toLowerCase().includes(search.toLowerCase()) ||
            member.username?.toLowerCase().includes(search.toLowerCase());
        
        const matchesGroup = activeTab === 'all' || 
            (activeTab === 'accounts' && selectedGroup && member.group_id === selectedGroup);
        
        const status = getMemberStatus(member);
        const matchesStatus = statusFilter === 'all' || status === statusFilter;
        
        return matchesSearch && matchesGroup && matchesStatus;
    });
    
    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-10 bg-muted rounded w-1/3"></div>
                    <div className="h-12 bg-muted rounded"></div>
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-16 bg-muted rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background" data-testid="members-page">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">Members Management</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {canCreateUsers() && (
                            <>
                                <Button variant="ghost" size="icon">
                                    <Upload className="w-5 h-5" />
                                </Button>
                                <Button 
                                    size="icon" 
                                    className="bg-primary"
                                    onClick={() => setShowAddDialog(true)}
                                    data-testid="add-member-btn"
                                >
                                    <Plus className="w-5 h-5" />
                                </Button>
                                <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="w-5 h-5" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <div className="border-b border-border">
                <div className="flex">
                    <button
                        onClick={() => { setActiveTab('all'); setSelectedGroup(null); }}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'all' 
                                ? 'border-primary text-primary' 
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="tab-all-members"
                    >
                        All Members
                    </button>
                    <DropdownMenu open={showGroupDropdown} onOpenChange={setShowGroupDropdown}>
                        <DropdownMenuTrigger asChild>
                            <button
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                                    activeTab === 'accounts' 
                                        ? 'border-primary text-primary' 
                                        : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid="tab-accounts"
                            >
                                Accounts
                                <ChevronDown className="w-4 h-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                            <div className="p-2 font-semibold text-sm text-muted-foreground">Brisbane Operations</div>
                            {groups.map((group) => (
                                <DropdownMenuItem
                                    key={group.id}
                                    onClick={() => {
                                        setActiveTab('accounts');
                                        setSelectedGroup(group.id);
                                        setShowGroupDropdown(false);
                                    }}
                                    className="pl-4"
                                    data-testid={`group-${group.id}`}
                                >
                                    {group.name}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <button
                        onClick={() => { setActiveTab('archived'); setSelectedGroup(null); }}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'archived' 
                                ? 'border-primary text-primary' 
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="tab-archived"
                    >
                        Archived
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="p-4 border-b border-border">
                <div className="flex flex-col sm:flex-row gap-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Filter by Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            {Object.entries(MEMBER_STATUSES).map(([value, { label }]) => (
                                <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search members"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-members"
                        />
                    </div>
                </div>
            </div>

            {/* Members Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                        <tr className="border-b">
                            <th className="w-10 p-3">
                                <input type="checkbox" className="rounded" />
                            </th>
                            <th className="p-3 text-left font-medium text-muted-foreground">Status</th>
                            <th className="p-3 text-left font-medium text-muted-foreground">Username</th>
                            <th className="p-3 text-left font-medium text-muted-foreground">First Name</th>
                            <th className="p-3 text-left font-medium text-muted-foreground">Last Name</th>
                            <th className="p-3 text-left font-medium text-muted-foreground hidden md:table-cell">Email</th>
                            <th className="p-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Mobile</th>
                            <th className="p-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Account</th>
                            <th className="p-3 text-left font-medium text-muted-foreground hidden xl:table-cell">Last Active</th>
                            <th className="w-10 p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMembers.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="p-8 text-center text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No members found</p>
                                </td>
                            </tr>
                        ) : (
                            filteredMembers.map((member) => {
                                const status = getMemberStatus(member);
                                const statusInfo = MEMBER_STATUSES[status] || MEMBER_STATUSES.active;
                                
                                return (
                                    <tr 
                                        key={member.id} 
                                        className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/members/${member.id}`)}
                                        data-testid={`member-row-${member.id}`}
                                    >
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" className="rounded" />
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="secondary" className={`text-xs ${statusInfo.color} text-white`}>
                                                {statusInfo.label}
                                            </Badge>
                                        </td>
                                        <td className="p-3 font-mono text-sm text-muted-foreground">{member.username}</td>
                                        <td className="p-3 font-medium">{member.first_name}</td>
                                        <td className="p-3">{member.last_name}</td>
                                        <td className="p-3 hidden md:table-cell text-muted-foreground">{member.email}</td>
                                        <td className="p-3 hidden lg:table-cell text-muted-foreground">{member.phone || '-'}</td>
                                        <td className="p-3 hidden lg:table-cell text-muted-foreground">{getGroupName(member.group_id)}</td>
                                        <td className="p-3 hidden xl:table-cell text-muted-foreground">{formatDate(member.last_active || member.created_at)}</td>
                                        <td className="p-3">
                                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Member Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add New Member</DialogTitle>
                        <DialogDescription>
                            Create a new member account. A temporary password will be generated.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddMember} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>First Name</Label>
                                <Input
                                    value={newMember.first_name}
                                    onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                                    required
                                    data-testid="new-member-firstname"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Last Name</Label>
                                <Input
                                    value={newMember.last_name}
                                    onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                                    required
                                    data-testid="new-member-lastname"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Username</Label>
                            <Input
                                value={newMember.username}
                                onChange={(e) => setNewMember({ ...newMember, username: e.target.value })}
                                required
                                data-testid="new-member-username"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={newMember.email}
                                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                                required
                                data-testid="new-member-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone (Optional)</Label>
                            <Input
                                value={newMember.phone}
                                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                                data-testid="new-member-phone"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select
                                value={newMember.role}
                                onValueChange={(value) => setNewMember({ ...newMember, role: value })}
                            >
                                <SelectTrigger data-testid="new-member-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRoles.map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            {role.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {groups.length > 0 && (
                            <div className="space-y-2">
                                <Label>Group (Optional)</Label>
                                <Select
                                    value={newMember.group_id || 'none'}
                                    onValueChange={(value) => setNewMember({ ...newMember, group_id: value === 'none' ? '' : value })}
                                >
                                    <SelectTrigger data-testid="new-member-group">
                                        <SelectValue placeholder="Select a group" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No group</SelectItem>
                                        {groups.map((group) => (
                                            <SelectItem key={group.id} value={group.id}>
                                                {group.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Member Status</Label>
                            <Select
                                value={newMember.member_status}
                                onValueChange={(value) => setNewMember({ ...newMember, member_status: value })}
                            >
                                <SelectTrigger data-testid="new-member-status">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(MEMBER_STATUSES).map(([value, { label }]) => (
                                        <SelectItem key={value} value={value}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-primary" data-testid="submit-new-member">
                                Create Member
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            
            {/* Welcome Message Dialog */}
            <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Member Created Successfully!</DialogTitle>
                        <DialogDescription>
                            Copy the message below and send it to the new member via your preferred email/messaging app.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="relative">
                            <Textarea
                                value={welcomeMessage}
                                readOnly
                                className="min-h-[200px] font-mono text-sm pr-12"
                                data-testid="welcome-message"
                            />
                            <Button
                                size="icon"
                                variant="ghost"
                                className="absolute top-2 right-2"
                                onClick={handleCopyWelcome}
                                data-testid="copy-welcome-btn"
                            >
                                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                        </div>
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                            <p className="text-sm text-amber-800 dark:text-amber-300">
                                <strong>Important:</strong> The temporary password shown above will only be displayed once. Make sure to copy this message before closing.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCopyWelcome} className="bg-primary gap-2">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? 'Copied!' : 'Copy Message'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowWelcomeDialog(false)}>
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
